import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { apiFetch } from "../api/client";
import {
  useTenantScope,
  normalizeScopePlatform,
} from "../scope/TenantScopeContext";
import { getSelectedCompanyId } from "../utils/tenantContext";

function getProfile() {
  for (const key of ["user", "currentUser", "profile"]) {
    try {
      const value = JSON.parse(
        localStorage.getItem(key) || "null"
      );

      if (value && typeof value === "object") {
        return value;
      }
    } catch (_) {
      // ignore invalid cached profile
    }
  }

  return {};
}

function severityForEvent(eventType) {
  const value = String(eventType || "").toLowerCase();

  if (
    value.includes("failure") ||
    value.includes("lockout")
  ) {
    return "warning";
  }

  return "info";
}

function normalizeBatch(batch) {
  return {
    id: `batch-${batch.batch_uuid}`,
    timestamp:
      batch.observed_at ||
      batch.created_at ||
      null,
    received_at: batch.created_at || null,
    device_uuid: batch.device_uuid || "",
    platform:
      normalizeScopePlatform(batch.platform) ||
      "other",
    event_type: "runtime_telemetry",
    severity: "info",
    source: "Runtime Telemetry",
    message:
      `Runtime telemetry received: ` +
      `${batch.process_count || 0} processes, ` +
      `${batch.service_count || 0} services, ` +
      `${batch.network_connection_count || 0} network connections, ` +
      `${batch.security_event_count || 0} security events.`,
  };
}

function normalizeSecurityEvent(event) {
  return {
    id: `security-${event.event_uuid}`,
    timestamp:
      event.event_time ||
      event.received_at ||
      null,
    received_at: event.received_at || null,
    device_uuid: event.device_uuid || "",
    platform: "windows",
    event_type:
      event.event_type ||
      "security_event",
    severity: severityForEvent(
      event.event_type
    ),
    source:
      event.provider ||
      "Windows Security",
    message: [
      event.account_name
        ? `Account: ${event.account_name}`
        : "",
      event.source_address
        ? `Source: ${event.source_address}`
        : "",
      event.logon_type
        ? `Logon type: ${event.logon_type}`
        : "",
      event.event_id
        ? `Event ID: ${event.event_id}`
        : "",
    ]
      .filter(Boolean)
      .join(" · "),
  };
}

function LogsPage() {
  const scope = useTenantScope();

  const profile = useMemo(
    () => getProfile(),
    []
  );

  const role =
    profile.role ||
    localStorage.getItem("role") ||
    "";

  const companyId =
    role === "super_admin"
      ? String(scope.companyId || "")
      : String(
          profile.company_id ||
          getSelectedCompanyId() ||
          ""
        );

  const [rows, setRows] = useState([]);
  const [severity, setSeverity] =
    useState("all");
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState("");

  const companySuffix =
    role === "super_admin"
      ? `?company_id=${encodeURIComponent(
          companyId
        )}`
      : "";

  const securitySuffix = (() => {
    const params = new URLSearchParams();

    if (
      role === "super_admin" &&
      companyId
    ) {
      params.set(
        "company_id",
        companyId
      );
    }

    if (scope.deviceUuid) {
      params.set(
        "device_uuid",
        scope.deviceUuid
      );
    }

    params.set("limit", "500");

    return `?${params.toString()}`;
  })();

  const load = useCallback(async () => {
    if (
      role === "super_admin" &&
      !companyId
    ) {
      setError(
        "Select a tenant company before loading logs."
      );
      setRows([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const batchPath =
        `/runtime-telemetry/batches` +
        (
          companySuffix
            ? `${companySuffix}&limit=500`
            : "?limit=500"
        );

      const [
        batchResponse,
        securityResponse,
      ] = await Promise.all([
        apiFetch(batchPath),
        apiFetch(
          `/runtime-telemetry/security-events${securitySuffix}`
        ),
      ]);

      const batchData =
        await batchResponse
          .json()
          .catch(() => []);

      const securityData =
        await securityResponse
          .json()
          .catch(() => []);

      if (!batchResponse.ok) {
        throw new Error(
          batchData?.detail ||
          `Runtime batch request failed (${batchResponse.status})`
        );
      }

      if (!securityResponse.ok) {
        throw new Error(
          securityData?.detail ||
          `Security event request failed (${securityResponse.status})`
        );
      }

      const normalized = [
        ...(Array.isArray(batchData)
          ? batchData.map(normalizeBatch)
          : []),

        ...(Array.isArray(securityData)
          ? securityData.map(
              normalizeSecurityEvent
            )
          : []),
      ];

      normalized.sort(
        (a, b) =>
          new Date(
            b.timestamp || 0
          ).getTime() -
          new Date(
            a.timestamp || 0
          ).getTime()
      );

      setRows(normalized);
    } catch (loadError) {
      console.error(
        "Modern logs load error:",
        loadError
      );

      setError(
        loadError.message ||
        "Unable to load telemetry logs."
      );

      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    companySuffix,
    role,
    scope.deviceUuid,
    securitySuffix,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(() => {
    return rows.filter((row) => {
      const deviceMatch =
        !scope.deviceUuid ||
        String(row.device_uuid) ===
          String(scope.deviceUuid);

      const platformMatch =
        !scope.platform ||
        scope.platform === "all" ||
        normalizeScopePlatform(
          row.platform
        ) === scope.platform;

      const severityMatch =
        severity === "all" ||
        String(
          row.severity || ""
        ).toLowerCase() === severity;

      return (
        deviceMatch &&
        platformMatch &&
        severityMatch
      );
    });
  }, [
    rows,
    scope.deviceUuid,
    scope.platform,
    severity,
  ]);

  return (
    <main
      style={{
        color: "#eef5fb",
        padding: 24,
        maxWidth: 1600,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          gap: 16,
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <h1>
            Endpoint Telemetry Logs
          </h1>

          <p
            style={{
              color: "#9fb3c5",
            }}
          >
            Live Windows and macOS
            operational telemetry from
            the modern endpoint runtime
            pipeline. Windows security
            and login events are included
            where supported.
          </p>
        </div>

        <button
          type="button"
          onClick={load}
          style={{
            padding: "10px 16px",
            borderRadius: 8,
          }}
        >
          Refresh
        </button>
      </div>

      <section
        style={{
          background: "#101923",
          border:
            "1px solid #263747",
          borderRadius: 12,
          padding: 16,
          marginBottom: 18,
        }}
      >
        <strong>
          Modern source
        </strong>

        <p
          style={{
            color: "#9fb3c5",
            marginBottom: 0,
          }}
        >
          Runtime telemetry batches are
          the canonical cross-platform
          operational log source.
          Historical legacy device_logs records are not used by this view. Modern runtime telemetry records are
          not substituted.
        </p>
      </section>

      {error ? (
        <section
          style={{
            background: "#291617",
            border:
              "1px solid #8b3030",
            borderRadius: 12,
            padding: 14,
            marginBottom: 18,
          }}
        >
          {error}
        </section>
      ) : null}

      <section
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 18,
        }}
      >
        <select
          value={severity}
          onChange={(event) =>
            setSeverity(
              event.target.value
            )
          }
          style={{
            padding: "9px 12px",
            borderRadius: 8,
          }}
        >
          <option value="all">
            All Severity
          </option>
          <option value="info">
            Info
          </option>
          <option value="warning">
            Warning
          </option>
        </select>

        <span>
          Visible records:{" "}
          <strong>
            {visibleRows.length}
          </strong>
        </span>
      </section>

      <section
        style={{
          overflowX: "auto",
          background: "#101923",
          border:
            "1px solid #263747",
          borderRadius: 12,
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse:
              "collapse",
          }}
        >
          <thead>
            <tr>
              {[
                "Observed",
                "Platform",
                "Device UUID",
                "Source",
                "Event",
                "Severity",
                "Details",
              ].map((name) => (
                <th
                  key={name}
                  style={{
                    textAlign:
                      "left",
                    padding: 12,
                    borderBottom:
                      "1px solid #263747",
                  }}
                >
                  {name}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="7"
                  style={{
                    padding: 20,
                  }}
                >
                  Loading telemetry...
                </td>
              </tr>
            ) : visibleRows.length ? (
              visibleRows.map(
                (row) => (
                  <tr key={row.id}>
                    <td
                      style={{
                        padding: 10,
                        borderBottom:
                          "1px solid #1d2b38",
                      }}
                    >
                      {row.timestamp
                        ? new Date(
                            row.timestamp
                          ).toLocaleString()
                        : "Unknown"}
                    </td>

                    <td
                      style={{
                        padding: 10,
                        borderBottom:
                          "1px solid #1d2b38",
                      }}
                    >
                      {row.platform}
                    </td>

                    <td
                      style={{
                        padding: 10,
                        borderBottom:
                          "1px solid #1d2b38",
                      }}
                    >
                      <code>
                        {row.device_uuid}
                      </code>
                    </td>

                    <td
                      style={{
                        padding: 10,
                        borderBottom:
                          "1px solid #1d2b38",
                      }}
                    >
                      {row.source}
                    </td>

                    <td
                      style={{
                        padding: 10,
                        borderBottom:
                          "1px solid #1d2b38",
                      }}
                    >
                      {row.event_type}
                    </td>

                    <td
                      style={{
                        padding: 10,
                        borderBottom:
                          "1px solid #1d2b38",
                      }}
                    >
                      {row.severity}
                    </td>

                    <td
                      style={{
                        padding: 10,
                        borderBottom:
                          "1px solid #1d2b38",
                      }}
                    >
                      {row.message ||
                        "—"}
                    </td>
                  </tr>
                )
              )
            ) : (
              <tr>
                <td
                  colSpan="7"
                  style={{
                    padding: 20,
                  }}
                >
                  No modern telemetry
                  records match the
                  selected scope.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </main>
  );
}

export default LogsPage;
